import Events from 'editor/components/Events';
import MoveBoxCPInteraction from 'editor/components/interactions/MoveBoxCPInteraction';
import { MapboxEventArgs } from 'editor/components/map/MapboxEvents/mapboxEventHandlers';
import Markup from 'editor/components/Markup';
import MarkupKit from 'editor/components/MarkupKit';
import {
  BoxControlPoint,
  boxControlPointHit,
  controlPointHitDelta,
} from 'editor/utils/controlPoints';
import { getSheetLayerHits } from 'editor/utils/map';
import MapSheetEvents from '../../MapSheetEvents';

const moveControlPointEvents = {
  mousedown(args: MapboxEventArgs<mapboxgl.MapEventType['mousedown']>): void {
    const { world, evt } = args;

    world.locateAll([Markup, MarkupKit]).forEach((entity) => {
      const markupComp = entity.get(Markup);
      const { MKPoint } = entity.get(MarkupKit);

      if (!markupComp.selected || markupComp.hidden) return;

      getSheetLayerHits(world, evt.lngLat.lng, evt.lngLat.lat).forEach((lh) => {
        const { entity: sheetLayerEntity, hit } = lh;

        if (!hit) return;
        const eventsComp = sheetLayerEntity.get(Events);
        const point = new MKPoint(hit.x, hit.y);

        const controlPointHit =
          boxControlPointHit(markupComp, point) !== BoxControlPoint.None;

        if (controlPointHit) {
          switch (markupComp.mkMarkup?.getType()) {
            case 'Square':
            case 'FreeText':
              {
                const cph = boxControlPointHit(markupComp, hit);

                if (cph !== BoxControlPoint.None) {
                  const mkm = markupComp.mkMarkup;

                  if (!mkm) {
                    return;
                  }

                  const delta = controlPointHitDelta(
                    markupComp,
                    cph,
                    hit.x,
                    hit.y
                  );

                  entity.add(eventsComp).add(
                    new MoveBoxCPInteraction({
                      originX: hit.x,
                      originY: hit.y,
                      currentX: hit.x,
                      currentY: hit.y,
                      deltaX: delta.x,
                      deltaY: delta.y,
                      controlPoint: cph,
                    })
                  );
                }
              }
              break;
            default:
          }
        }
      });
    });
  },

  mousemove(args: MapboxEventArgs<mapboxgl.MapEventType['mousemove']>): void {
    const { world, evt } = args;

    world.grabAll(MoveBoxCPInteraction).forEach(({ entity, component }) => {
      getSheetLayerHits(world, evt.lngLat.lng, evt.lngLat.lat).forEach((lh) => {
        const { hit } = lh;

        if (!hit) return;

        const { MKPoint } = entity.get(MarkupKit);
        const { x, y } = new MKPoint(hit.x, hit.y);

        component.currentX = x;
        component.currentY = y;
      });
    });
  },

  mouseup(args: MapboxEventArgs<mapboxgl.MapEventType['mouseup']>): void {
    // Save moved Markup
    const { world } = args;

    world.locateAll(MoveBoxCPInteraction).forEach((entity) => {
      const markupComp = entity.get(Markup);
      const { markup, mkMarkup } = markupComp;

      if (mkMarkup) {
        mkMarkup.setModDate(new Date().toISOString());
        const jsonBlob = JSON.stringify(mkMarkup.getAsObject());
        const nextMarkup = markup.set('jsonBlob', jsonBlob);

        if (entity.has(Events)) {
          const events = entity.get<Events<MapSheetEvents>>(Events);

          events.events.emit('markup:move', { markup: nextMarkup });
        }
      }

      entity.remove(MoveBoxCPInteraction);
      entity.remove(Events);
    });
  },

  touchstart(args: MapboxEventArgs<mapboxgl.MapEventType['touchstart']>): void {
    const { world, evt } = args;

    world.locateAll([Markup, MarkupKit]).forEach((entity) => {
      const markupComp = entity.get(Markup);
      const { MKPoint } = entity.get(MarkupKit);

      if (!markupComp.selected || markupComp.hidden) return;

      getSheetLayerHits(world, evt.lngLat.lng, evt.lngLat.lat).forEach((lh) => {
        const { entity: sheetLayerEntity, hit } = lh;

        if (!hit) return;
        const eventsComp = sheetLayerEntity.get(Events);
        const point = new MKPoint(hit.x, hit.y);

        const controlPointHit =
          boxControlPointHit(markupComp, point) !== BoxControlPoint.None;

        if (controlPointHit) {
          switch (markupComp.mkMarkup?.getType()) {
            case 'Square':
            case 'FreeText':
              {
                const cph = boxControlPointHit(markupComp, hit);

                if (cph !== BoxControlPoint.None) {
                  const mkm = markupComp.mkMarkup;

                  if (!mkm) {
                    return;
                  }

                  const delta = controlPointHitDelta(
                    markupComp,
                    cph,
                    hit.x,
                    hit.y
                  );

                  entity.add(eventsComp).add(
                    new MoveBoxCPInteraction({
                      originX: hit.x,
                      originY: hit.y,
                      currentX: hit.x,
                      currentY: hit.y,
                      deltaX: delta.x,
                      deltaY: delta.y,
                      controlPoint: cph,
                    })
                  );
                }
              }
              break;
            default:
          }
        }
      });
    });
  },

  touchmove: (
    args: MapboxEventArgs<mapboxgl.MapEventType['touchmove']>
  ): void => {
    const { world, evt } = args;

    world.grabAll(MoveBoxCPInteraction).forEach(({ entity, component }) => {
      getSheetLayerHits(world, evt.lngLat.lng, evt.lngLat.lat).forEach((lh) => {
        const { hit } = lh;

        if (!hit) return;

        const { MKPoint } = entity.get(MarkupKit);
        const { x, y } = new MKPoint(hit.x, hit.y);

        component.currentX = x;
        component.currentY = y;
      });
    });
  },

  touchend(args: MapboxEventArgs<mapboxgl.MapEventType['touchend']>): void {
    // Save moved Markup
    const { world } = args;

    world.locateAll(MoveBoxCPInteraction).forEach((entity) => {
      const markupComp = entity.get(Markup);
      const { markup, mkMarkup } = markupComp;

      if (mkMarkup) {
        mkMarkup.setModDate(new Date().toISOString());
        const jsonBlob = JSON.stringify(mkMarkup.getAsObject());
        const nextMarkup = markup.set('jsonBlob', jsonBlob);

        if (entity.has(Events)) {
          const events = entity.get<Events<MapSheetEvents>>(Events);

          events.events.emit('markup:move', { markup: nextMarkup });
        }
      }

      entity.remove(MoveBoxCPInteraction);
      entity.remove(Events);
    });
  },
};

export default moveControlPointEvents;
